/**
 * Role-Based Access Control (RBAC) System
 * Manages user roles, permissions, and access control
 */

// User roles
export type UserRole =
    | "admin"
    | "manager"
    | "technician"
    | "accountant"
    | "client";

// Permission types
export type Permission =
    | "workorders:read"
    | "workorders:write"
    | "workorders:delete"
    | "inventory:read"
    | "inventory:write"
    | "inventory:delete"
    | "invoices:read"
    | "invoices:write"
    | "invoices:delete"
    | "invoices:issue"
    | "invoices:cancel"
    | "quotes:read"
    | "quotes:write"
    | "quotes:delete"
    | "quotes:approve"
    | "reports:read"
    | "reports:export"
    | "clients:read"
    | "clients:write"
    | "clients:delete"
    | "users:read"
    | "users:write"
    | "users:delete"
    | "settings:read"
    | "settings:write"
    | "documents:read"
    | "documents:write"
    | "documents:delete";

// Role permissions mapping
export const rolePermissions: Record<UserRole, Permission[]> = {
    admin: [
        // Full access to everything
        "workorders:read",
        "workorders:write",
        "workorders:delete",
        "inventory:read",
        "inventory:write",
        "inventory:delete",
        "invoices:read",
        "invoices:write",
        "invoices:delete",
        "invoices:issue",
        "invoices:cancel",
        "quotes:read",
        "quotes:write",
        "quotes:delete",
        "quotes:approve",
        "reports:read",
        "reports:export",
        "clients:read",
        "clients:write",
        "clients:delete",
        "users:read",
        "users:write",
        "users:delete",
        "settings:read",
        "settings:write",
        "documents:read",
        "documents:write",
        "documents:delete",
    ],
    manager: [
        // Manager access (no user management)
        "workorders:read",
        "workorders:write",
        "workorders:delete",
        "inventory:read",
        "inventory:write",
        "invoices:read",
        "invoices:write",
        "invoices:issue",
        "quotes:read",
        "quotes:write",
        "quotes:approve",
        "reports:read",
        "reports:export",
        "clients:read",
        "clients:write",
        "users:read",
        "settings:read",
        "documents:read",
        "documents:write",
    ],
    technician: [
        // Technician access (field work)
        "workorders:read",
        "workorders:write",
        "inventory:read",
        "inventory:write",
        "quotes:read",
        "clients:read",
        "documents:read",
        "documents:write",
    ],
    accountant: [
        // Accountant access (financial)
        "invoices:read",
        "invoices:write",
        "invoices:issue",
        "invoices:cancel",
        "quotes:read",
        "reports:read",
        "reports:export",
        "clients:read",
        "documents:read",
    ],
    client: [
        // Client access (view only their data)
        "documents:read",
        "invoices:read",
        "quotes:read",
    ],
};

// User interface with role
export interface User {
    id: string;
    email: string;
    name: string;
    role: UserRole;
    active: boolean;
    createdAt: string;
    lastLogin?: string;
    // GDPR
    consentGiven: boolean;
    consentDate?: string;
    dataRetentionUntil?: string;
}

// Session interface
export interface UserSession {
    user: User;
    token: string;
    expiresAt: string;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(user: User, permission: Permission): boolean {
    const permissions = rolePermissions[user.role];
    return permissions.includes(permission);
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
    user: User,
    permissions: Permission[]
): boolean {
    return permissions.some((permission) => hasPermission(user, permission));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
    user: User,
    permissions: Permission[]
): boolean {
    return permissions.every((permission) => hasPermission(user, permission));
}

/**
 * Get all permissions for a user role
 */
export function getRolePermissions(role: UserRole): Permission[] {
    return rolePermissions[role];
}

/**
 * Check if role has sufficient privileges (higher or equal in hierarchy)
 */
export function hasRolePrivileges(
    userRole: UserRole,
    requiredRole: UserRole
): boolean {
    const roleHierarchy: Record<UserRole, number> = {
        client: 0,
        technician: 1,
        accountant: 2,
        manager: 3,
        admin: 4,
    };

    return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Get role display name in Spanish
 */
export function getRoleDisplayName(role: UserRole): string {
    const displayNames: Record<UserRole, string> = {
        admin: "Administrador",
        manager: "Gerente",
        technician: "Técnico",
        accountant: "Contable",
        client: "Cliente",
    };

    return displayNames[role];
}

/**
 * Get permission display name in Spanish
 */
export function getPermissionDisplayName(permission: Permission): string {
    const displayNames: Record<Permission, string> = {
        "workorders:read": "Ver órdenes de trabajo",
        "workorders:write": "Crear/editar órdenes de trabajo",
        "workorders:delete": "Eliminar órdenes de trabajo",
        "inventory:read": "Ver inventario",
        "inventory:write": "Gestionar inventario",
        "inventory:delete": "Eliminar inventario",
        "invoices:read": "Ver facturas",
        "invoices:write": "Crear/editar facturas",
        "invoices:delete": "Eliminar facturas",
        "invoices:issue": "Emitir facturas",
        "invoices:cancel": "Anular facturas",
        "quotes:read": "Ver presupuestos",
        "quotes:write": "Crear/editar presupuestos",
        "quotes:delete": "Eliminar presupuestos",
        "quotes:approve": "Aprobar presupuestos",
        "reports:read": "Ver informes",
        "reports:export": "Exportar informes",
        "clients:read": "Ver clientes",
        "clients:write": "Gestionar clientes",
        "clients:delete": "Eliminar clientes",
        "users:read": "Ver usuarios",
        "users:write": "Gestionar usuarios",
        "users:delete": "Eliminar usuarios",
        "settings:read": "Ver configuración",
        "settings:write": "Modificar configuración",
        "documents:read": "Ver documentos",
        "documents:write": "Crear/editar documentos",
        "documents:delete": "Eliminar documentos",
    };

    return displayNames[permission];
}

export default {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getRolePermissions,
    hasRolePrivileges,
    getRoleDisplayName,
    getPermissionDisplayName,
};
